import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { FullCalendarModule } from '@fullcalendar/angular';
import { IWizRoute, WizModule, WizNavigationManager } from '@wizeoo/akm-component-lib';
import { AgGridModule } from 'ag-grid-angular';
import { GridsterModule } from 'angular-gridster2';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { AppComponent } from './app.component';
import { APIHttpInterceptor } from './manager/http/APIHttpInterceptor';
import { initializeKeycloak } from './manager/http/keycloak-initializer';
import { AppInitializer } from './manager/app-init';
import { MdBusinessUnitTileRenderer } from './views/business-unit/renderers/md-bu-tile.renderer';
import { ChronocodeMainView } from './views/chronocode/chronocode-main.view';
import { ChronoLevelCreationGroup } from './views/chronocode/creation/chrono-level-creation.group';
import { ChronocodeCreationView } from './views/chronocode/creation/chronocode-creation.view';
import { ChronocodeElementView } from './views/chronocode/element/chronocode-element.view';
import { ChronocodeHomeView } from './views/chronocode/home/chronocode-home.view';
import { ChronoDetailEditRenderer } from './views/chronocode/renderer/edit/chrono-detail-edit.renderer';
import { ChronocodeLevelRenderer } from './views/chronocode/renderer/edit/chronocode-level.renderer';
import { ChronocodeTreeRenderer } from './views/chronocode/renderer/edit/chronocode.tree.renderer';
import { AgChronocodeExampleActionRenderer } from './views/chronocode/renderer/home/ag-chronocode-example-action.renderer';
import { MdChronocodeExampleTileRenderer } from './views/chronocode/renderer/home/md-chronocode-example-tile.renderer';
import { ChronocodeSmallRenderer } from './views/chronocode/renderer/main/chronocode-small.renderer';
import { MdChronoCodeTileRenderer } from './views/chronocode/renderer/main/md-chronocode-tile.renderer';
import { DashboardDataView } from './views/dashboard/datas/dashboard-data.view';
import { FallbackView } from './views/fallback/fallback.view';
import { BuIconRenderer } from './views/fallback/renderers/bu-icon.renderer';
import { VisionIconRenderer } from './views/fallback/renderers/vision-icon.renderer';
import { VisionTileRenderer } from './views/fallback/renderers/vision-tile.renderer';
import { FormatLockRenderer } from './views/formats/format-lock.renderer';
import { FormatView } from './views/formats/format.view';
import { CssRenderView } from './views/cssrender/cssrender.view'; 
import { WorkflowView } from './views/workflow/workflow.view';
import { FreedataView } from './views/freedatas/freedata.view';
import { AgFreedataExampleActionRenderer } from './views/freedatas/renderers/action/ag-freedata-example-action.renderer';
import { FDLevelRenderer } from './views/freedatas/renderers/fd-level.renderer';
import { FreedataValueTreeRenderer } from './views/freedatas/renderers/fdvalue-tree.renderer';
import { MdFreedataExampleTileRenderer } from './views/freedatas/renderers/md-freedata-example-tile.renderer';
import { MdFreedataTileRenderer } from './views/freedatas/renderers/md-freedata-tile.renderer';
import { IncExcView } from './views/incexc/incexc.view';
import { IncExcSamplePop } from './views/incexc/pop/incexc-sample.pop';
import { IncExcContentRenderer } from './views/incexc/renderer/incex-content.renderer';
import { IncExcKeyPartRenderer } from './views/incexc/renderer/incex-keypart.renderer';
import { IncExcKindRenderer } from './views/incexc/renderer/incex-kind.renderer';
import { IncExcTileRenderer } from './views/incexc/renderer/incex-tile.renderer';
import { IncExcWayRenderer } from './views/incexc/renderer/incex-way.renderer';
import { InformationBar } from './views/main/information/information.bar';
import { NavigationBar } from './views/main/menu/menu.bar';
import { MenuPopUp } from './views/main/menu/menu.pop';
import { TabBar } from './views/main/tabs/tab.bar';
import { MeasureCanvas } from './views/measure/measure-canvas';
import { MeasureView } from './views/measure/measure.view';
import { MeasureTreeRenderer } from './views/measure/renderers/measure-tree.renderer';
import { NewtabView } from './views/newtab/newtab.view';
import { NewTabPop } from './views/newtab/pop/new-tab.pop';
import { TabRenderer } from './views/newtab/renderer/tab.renderer';
import { ProfileView } from './views/profile/profile.view';
import { RootView } from './views/root/root.view';
import { StyleView } from './views/style/style.view';
import { PhysicalTemplateCreationPop } from './views/templating/pop/phys-template.create.pop';
import { TemplatingView } from './views/templating/templating.view';
import { UserTabView } from './views/user-tab/user-tab.view';
import { UserView } from './views/user/user.view';
import { OrderedVisionKeyRenderer } from './views/vision/renderer/ordered-vision-key.renderer';
import { StepKeyRenderer } from './views/vision/renderer/step-key.renderer';
import { VisionMeasureListRenderer } from './views/vision/renderer/vision-measure-list.renderer';
import { VisionMeasureRenderer } from './views/vision/renderer/vision-measure.renderer';
import { VisionThumbRenderer } from './views/vision/renderer/vision-thumb.renderer';
import { VisionStepRenderer } from './views/vision/renderer/vision-step.renderer';
import { VisionGenerateView } from './views/vision/vision-generate.view';
import { VisionView } from './views/vision/vision.view';
import { UserPopUp } from './views/main/menu/user.pop';
import { EditTabPop } from './views/newtab/pop/edit-tab.pop';
import { FreedataValueCodePop } from './views/freedatas/pops/fdv-code.pop';
import { StepEditPop } from './views/vision/renderer/step-edit.pop';
import { ConnectView } from './views/connect/connect.view';
import { ArgAddPop } from './views/freedatas/pops/arg.add.pop';
import { ServiceWorkerModule } from '@angular/service-worker';
import { WgView } from './views/wg/wg.view';
import { DomIconRenderer } from './views/fallback/renderers/dom-icon.renderer';
import { ScenarioView } from './views/scenarii/scenario.view';
import { ScenTreeRenderer } from './views/scenarii/scen-tree.renderer';
import { ScenStepRenderer } from './views/scenarii/scen-step.renderer';
import { CommonModule, InjectionService, NavigationEngine, PivotCellEditor, PivotHeaderGroupRenderer, TooltipService } from '@wizeoo/akm-common-lib';
import { AttachmentService } from '@wizeoo/akm-common-lib';
import { SwUpdateService } from './manager/worker/sw-update.service';
import { MeasureRightPop } from './views/measure/pops/measure-right-pop';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { RouteEngine } from './manager/routing/route.engine';
import { Model } from './model/model';
import { MatrixView } from './views/matrix/matrix.view';


@NgModule({
  declarations: [
    MeasureRightPop,
    AppComponent,
    FreedataView,
    FreedataValueTreeRenderer, FreedataValueCodePop,
    FDLevelRenderer, ArgAddPop,
    MeasureView,
    VisionView,
    VisionGenerateView,
    VisionMeasureRenderer, VisionThumbRenderer,
    VisionMeasureListRenderer,
    StepEditPop,
    OrderedVisionKeyRenderer,
    TemplatingView,
    ChronocodeSmallRenderer,
    ChronocodeCreationView,
    ChronoLevelCreationGroup,
    ChronoDetailEditRenderer,
    ChronocodeMainView,
    ChronocodeElementView,
    ChronocodeHomeView,
    ChronocodeLevelRenderer,
    MdChronocodeExampleTileRenderer,
    AgChronocodeExampleActionRenderer,
    NewtabView,
    InformationBar,
    NavigationBar,
    TabBar,
    MenuPopUp, UserPopUp,
    ChronocodeTreeRenderer,
    MdChronoCodeTileRenderer,
    MdFreedataTileRenderer,
    AgFreedataExampleActionRenderer,
    MdFreedataExampleTileRenderer,
    MdBusinessUnitTileRenderer,
    ProfileView,
    DashboardDataView,
    MeasureCanvas,
    MeasureTreeRenderer,
    TabRenderer,
    VisionStepRenderer,
    StepKeyRenderer,
    PhysicalTemplateCreationPop,
    NewTabPop, EditTabPop,
    UserTabView,
    UserView,
    IncExcView,
    IncExcTileRenderer,
    IncExcKindRenderer,
    IncExcContentRenderer,
    IncExcKeyPartRenderer,
    IncExcWayRenderer,
    IncExcSamplePop,
    StyleView,
    ConnectView,
    FormatView,
    CssRenderView,
    WorkflowView,
    MatrixView,
    FormatLockRenderer,
    FallbackView,
    VisionTileRenderer,
    BuIconRenderer, DomIconRenderer,
    VisionIconRenderer,
    RootView,
    WgView,
    ScenarioView, ScenTreeRenderer, ScenStepRenderer,
    
  ],
  imports: [
    MatDialogModule,
    WizModule,
    CommonModule,
    GridsterModule,
    FullCalendarModule,
    RouterModule.forRoot(RouteEngine.getRouteDefinition(), { useHash: false }),
    KeycloakAngularModule,
    BrowserAnimationsModule,
    HttpClientModule, 
    AgGridModule.withComponents([PivotCellEditor, PivotHeaderGroupRenderer]),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      // registrationStrategy: 'registerWhenStable:30000',
      registrationStrategy: 'registerImmediately'
    })
  ],
  providers: [
    MatDialogModule,
    AppInitializer, // lancement des services d'init
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService, AppInitializer],
    },
    WizModule,
    CommonModule,
    { provide: HTTP_INTERCEPTORS, useClass: APIHttpInterceptor, multi: true }, // Intercepteur pour construction de l'URL http correcte
    InjectionService,
    TooltipService,
    AttachmentService,
    SwUpdateService // lancement du check de version du serviceWorker
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    private router: Router,
    private attachmentService: AttachmentService, // pour init et alimentation de l'httpClient
    private swUpdateService: SwUpdateService
  ) {
    
    let mod: Model = Model.getInstance();
    
    // construction des routes depuis NavigationEngine
    const dynaRoutes: IWizRoute[] = NavigationEngine.buildRoute(RouteEngine.getRouteDefinition(), '');
    // mise à jour des routes (construction des fullPaths)
    this.router.resetConfig(dynaRoutes);
    // on force l'injection du modèle sur le routeur
    this.router['model'] = mod;
    // mémorisation des routes sur le modèle, pour utilisation par le tabEngine
    mod.routes = RouteEngine.getRouteDefinition(); 
    // mémorisation de la vue "vision" comme ajout systématique dans les tabs
    mod.favoriteRoute = RouteEngine.VISION_VIEW;
    // alimentation du router sur navigation
    WizNavigationManager.router = this.router; 
  }
}
